import {
  COLLECTIONS,
  unifiedAddDoc,
  unifiedCollection,
  unifiedDeleteDoc,
  unifiedGetDoc,
  unifiedUpdateDoc,
  Timestamp,
  deleteField,
  type CrewPostLinks,
} from '@watchlist/shared';
import { timestampOrMillis } from '../utils/crewFeedUtils';
import { likeCrewPostWithRetry } from './functionsApi';

const DEFAULT_CREW_POSTS_LIMIT = 80;

/** Maximum crew posts that can be actively pinned at the same time */
export const MAX_PINNED_CREW_POSTS = 3;

const MS_PER_DAY = 86400000;

export async function fetchCrewPostsForFeed(
  limitCount: number = DEFAULT_CREW_POSTS_LIMIT
): Promise<Array<{ id: string; data: Record<string, unknown> }>> {
  const now = Timestamp.now();
  const merged = new Map<string, { id: string; data: Record<string, unknown> }>();

  try {
    const pinnedSnap = await unifiedCollection(COLLECTIONS.CREW_POSTS).where('pinExpiresAt', '>', now).getDocs();
    for (const d of pinnedSnap) {
      merged.set(d.id, { id: d.id, data: d.data() as Record<string, unknown> });
    }
  } catch (e) {
    console.warn('crewPostService: pinned crewPosts query failed', e);
  }

  const recentSnap = await unifiedCollection(COLLECTIONS.CREW_POSTS)
    .orderBy('createdAt', 'desc')
    .limit(limitCount)
    .getDocs();

  for (const d of recentSnap) {
    if (!merged.has(d.id)) {
      merged.set(d.id, { id: d.id, data: d.data() as Record<string, unknown> });
    }
  }

  return Array.from(merged.values());
}

/** Active pins (pinExpiresAt > now), optionally excluding one document id. */
export async function countActivePinsExcluding(excludePostId: string | null): Promise<number> {
  const now = Timestamp.now();
  const docs = await unifiedCollection(COLLECTIONS.CREW_POSTS).where('pinExpiresAt', '>', now).getDocs();
  if (!excludePostId) return docs.length;
  return docs.filter(d => d.id !== excludePostId).length;
}

export async function createCrewPost(params: {
  authorUid: string;
  title: string;
  subtitle?: string;
  links: CrewPostLinks;
  /** Pin to top for this many days (subject to {@link MAX_PINNED_CREW_POSTS}). */
  pinDays?: number;
}): Promise<string> {
  const pinDays = params.pinDays && params.pinDays > 0 ? Math.min(Math.floor(params.pinDays), 365) : 0;

  if (pinDays > 0) {
    const n = await countActivePinsExcluding(null);
    if (n >= MAX_PINNED_CREW_POSTS) {
      throw new Error(
        `At most ${MAX_PINNED_CREW_POSTS} posts can be pinned. Unpin one or wait for a pin to expire.`
      );
    }
  }

  const now = Date.now();
  const doc: Record<string, unknown> = {
    title: params.title.trim(),
    authorUid: params.authorUid,
    createdAt: Timestamp.fromMillis(now),
    /** Native Firestore bridge may omit Timestamp fields; clients use this for sort + relative time. */
    createdAtMillis: now,
    links: pruneLinksForFirestore(params.links),
  };
  const subtitleTrimmed = params.subtitle?.trim();
  if (subtitleTrimmed) {
    doc.subtitle = subtitleTrimmed;
  }

  if (pinDays > 0) {
    const pinExpiresAtMillis = now + pinDays * MS_PER_DAY;
    doc.pinnedAt = Timestamp.fromMillis(now);
    doc.pinExpiresAt = Timestamp.fromMillis(pinExpiresAtMillis);
    doc.pinnedAtMillis = now;
    doc.pinExpiresAtMillis = pinExpiresAtMillis;
  }

  return unifiedAddDoc(COLLECTIONS.CREW_POSTS, doc);
}

export async function updateCrewPostPin(postId: string, pinDays: number | null): Promise<void> {
  if (pinDays == null || pinDays <= 0) {
    await unifiedUpdateDoc(COLLECTIONS.CREW_POSTS, postId, {
      pinExpiresAt: deleteField(),
      pinnedAt: deleteField(),
      pinExpiresAtMillis: deleteField(),
      pinnedAtMillis: deleteField(),
    });
    return;
  }

  const days = Math.min(Math.floor(pinDays), 365);
  const snap = await unifiedGetDoc(COLLECTIONS.CREW_POSTS, postId);
  const docData = snap?.exists?.() ? snap.data() : {};
  const exp = timestampOrMillis(docData as Record<string, unknown>, 'pinExpiresAt', 'pinExpiresAtMillis');
  const currentlyPinned = exp > Date.now();

  const otherPins = await countActivePinsExcluding(postId);
  if (!currentlyPinned && otherPins >= MAX_PINNED_CREW_POSTS) {
    throw new Error(
      `At most ${MAX_PINNED_CREW_POSTS} posts can be pinned. Unpin one or wait for a pin to expire.`
    );
  }

  const t = Date.now();
  const pinExpiresAtMillis = t + days * MS_PER_DAY;
  await unifiedUpdateDoc(COLLECTIONS.CREW_POSTS, postId, {
    pinExpiresAt: Timestamp.fromMillis(pinExpiresAtMillis),
    pinnedAt: Timestamp.fromMillis(t),
    pinExpiresAtMillis,
    pinnedAtMillis: t,
  });
}

export async function deleteCrewPost(postId: string): Promise<void> {
  await unifiedDeleteDoc(COLLECTIONS.CREW_POSTS, postId);
}

export async function toggleCrewPostLike(postId: string, userId: string, action: 'like' | 'unlike'): Promise<void> {
  await likeCrewPostWithRetry(postId, action);
}

/**
 * Firestore rejects `undefined` anywhere in document data. Build a plain map with only
 * non-empty arrays so `links` is always valid (including `{}` when there are no deep links).
 */
function pruneLinksForFirestore(links: CrewPostLinks): Record<string, string[]> {
  const pick = (arr?: string[]) => {
    const u = arr?.map(s => s.trim()).filter(Boolean);
    return u?.length ? u : null;
  };
  const out: Record<string, string[]> = {};
  const w = pick(links.watchlistIds);
  if (w) out.watchlistIds = w;
  const m = pick(links.movieIds);
  if (m) out.movieIds = m;
  const r = pick(links.reviewIds);
  if (r) out.reviewIds = r;
  const u = pick(links.userIds);
  if (u) out.userIds = u;
  const p = pick(links.personIds);
  if (p) out.personIds = p;
  return out;
}
